import React, { useState, useEffect } from 'react';
import { API_CONFIG } from '../config/api';
import '../styles/pages/_data-permissions.scss';

interface AssessmentType {
  id: string;
  name: string;
  category: 'wellness' | 'learn';
  description: string;
}

interface ChatbotPermission {
  chatbotId: string;
  chatbotName: string;
  allowedAssessments: string[];
  icon: string;
  avatarUrl?: string;
}

const DataPermissionsPage: React.FC = () => {
  const [permissions, setPermissions] = useState<ChatbotPermission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [expandedSections, setExpandedSections] = useState<Record<string, { wellness: boolean; learn: boolean }>>({});

  // Define all available assessments
  const assessmentTypes: AssessmentType[] = [
    // Wellness Tab Assessments
    { id: 'physical-health', name: 'Physical Wellness Assessment', category: 'wellness', description: 'Height, weight, BMI, chronic conditions' },
    { id: 'mental-health', name: 'Mental Wellness Assessment', category: 'wellness', description: 'Mental wellness evaluation' },
    { id: 'emotional-wellbeing', name: 'Emotional Wellbeing Assessment', category: 'wellness', description: 'Emotional state evaluation' },
    { id: 'blood-test', name: 'Blood Test Records', category: 'wellness', description: 'Laboratory test results and metrics' },
    
    // Learn Tab Assessments
    { id: 'spiritual', name: 'Spiritual Assessment', category: 'learn', description: 'Comprehensive spiritual wellbeing evaluation (includes FASDL and FACIT-Sp-12)' },
    { id: 'nutritional', name: 'Nutritional Assessment (Phil-HEI)', category: 'learn', description: 'Filipino nutrition and diet quality' },
    { id: 'emotio-psycho-rational', name: 'Emotio-Psycho-Rational Assessment', category: 'learn', description: 'Comprehensive emotional, psychological, and rational evaluation (includes DASS-21, PHQ-9, GAD-7, Gender Sensitivity)' },
    
    // New Assessment Types
    { id: 'legendarial', name: 'Legendarial Assessment', category: 'learn', description: 'Heritage, identity, and cultural narrative evaluation' },
    { id: 'financial', name: 'Financial Assessment', category: 'learn', description: 'Financial stability and resource management evaluation' },
    { id: 'religio-traditio-cultural', name: 'Religio-Traditio-Cultural Assessment', category: 'learn', description: 'Religious, traditional, and cultural practices evaluation' },
    { id: 'environmental', name: 'Environmental Assessment', category: 'learn', description: 'Environmental factors and their impact on wellbeing' },
    { id: 'socio-relational', name: 'Socio-Relational Assessment', category: 'learn', description: 'Social and relational thinking patterns evaluation' },
    { id: 'occupational', name: 'Occupational Assessment', category: 'learn', description: 'Work-life balance and occupational wellbeing evaluation' }
  ];

  // Define chatbots (12 coaches)
  const chatbots: ChatbotPermission[] = [
    {
      chatbotId: 'life-psensor',
      chatbotName: 'LIFE PSENsOR',
      icon: '🤖',
      avatarUrl: '/BackgroundSamples/Spiritual.png', // Using Spiritual background for main coach
      allowedAssessments: []
    },
    {
      chatbotId: 'fitness-diet',
      chatbotName: 'Physical Fitness Coach',
      icon: '💪',
      avatarUrl: '/BackgroundSamples/Phyisical.png',
      allowedAssessments: []
    },
    {
      chatbotId: 'wellness',
      chatbotName: 'Wellness Coach',
      icon: '🧘',
      avatarUrl: '/BackgroundSamples/Emotio-Psycho-Rational.png', // Using Emotional background for general wellness
      allowedAssessments: []
    },
    {
      chatbotId: 'spiritual',
      chatbotName: 'Spiritual Coach',
      icon: '🕊️',
      avatarUrl: '/BackgroundSamples/Spiritual.png',
      allowedAssessments: []
    },
    {
      chatbotId: 'nutritional',
      chatbotName: 'Nutritional Coach',
      icon: '🥗',
      avatarUrl: '/BackgroundSamples/Nutritional.png',
      allowedAssessments: []
    },
    {
      chatbotId: 'emotio_psycho_rational',
      chatbotName: 'Emotio-Psycho-Rational Coach',
      icon: '🧩',
      avatarUrl: '/BackgroundSamples/Emotio-Psycho-Rational.png',
      allowedAssessments: []
    },
    {
      chatbotId: 'legendarial',
      chatbotName: 'Legendarial Coach',
      icon: '🛏️',
      avatarUrl: '/BackgroundSamples/Legendarial.png',
      allowedAssessments: []
    },
    {
      chatbotId: 'religio_traditio_cultural',
      chatbotName: 'Religio-Traditio-Cultural Coach',
      icon: '🕯️',
      avatarUrl: '/BackgroundSamples/Religio-Traditio-Cultural.png',
      allowedAssessments: []
    },
    {
      chatbotId: 'socio_relational',
      chatbotName: 'Socio-Relational Coach',
      icon: '🗣️',
      avatarUrl: '/BackgroundSamples/Socio-Relational.png',
      allowedAssessments: []
    },
    {
      chatbotId: 'financial',
      chatbotName: 'Financial Coach',
      icon: '💰',
      avatarUrl: '/BackgroundSamples/Financial.png',
      allowedAssessments: []
    },
    {
      chatbotId: 'environmental',
      chatbotName: 'Environmental Coach',
      icon: '🌍',
      avatarUrl: '/BackgroundSamples/Environmental.png',
      allowedAssessments: []
    },
    {
      chatbotId: 'occupational',
      chatbotName: 'Occupational Coach',
      icon: '💼',
      avatarUrl: '/BackgroundSamples/Occupational.png',
      allowedAssessments: []
    }
  ];

  useEffect(() => {
    fetchPermissions();
  }, []);

  const fetchPermissions = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setPermissions(chatbots);
        setIsLoading(false);
        return;
      }

      const response = await fetch(API_CONFIG.ENDPOINTS.DATA_PERMISSIONS, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        if (data && data.success && data.data && data.data.permissions) {
          // Ensure all coaches are present, add missing ones from chatbots array
          const apiPermissions: ChatbotPermission[] = data.data.permissions;
          const mergedPermissions = chatbots.map(chatbot => {
            const existingPermission = apiPermissions.find((p: ChatbotPermission) => p.chatbotId === chatbot.chatbotId);
            return existingPermission || chatbot;
          });
          setPermissions(mergedPermissions);
        } else {
          // Initialize with empty permissions (no default permissions)
          setPermissions(chatbots);
        }
      } else {
        // Initialize with empty permissions (no default permissions)
        setPermissions(chatbots);
      }
    } catch (error) {

      setPermissions(chatbots);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleAssessment = (chatbotId: string, assessmentId: string) => {
    setPermissions(prevPermissions => 
      prevPermissions.map(chatbot => {
        if (chatbot.chatbotId === chatbotId) {
          const isCurrentlyAllowed = chatbot.allowedAssessments.includes(assessmentId);
          return {
            ...chatbot,
            allowedAssessments: isCurrentlyAllowed
              ? chatbot.allowedAssessments.filter(id => id !== assessmentId)
              : [...chatbot.allowedAssessments, assessmentId]
          };
        }
        return chatbot;
      })
    );
  };

  const toggleAllForChatbot = (chatbotId: string, category: 'wellness' | 'learn' | 'all') => {
    setPermissions(prevPermissions =>
      prevPermissions.map(chatbot => {
        if (chatbot.chatbotId === chatbotId) {
          let assessmentsToToggle: string[];
          
          if (category === 'all') {
            assessmentsToToggle = assessmentTypes.map(a => a.id);
          } else {
            assessmentsToToggle = assessmentTypes
              .filter(a => a.category === category)
              .map(a => a.id);
          }

          const allSelected = assessmentsToToggle.every(id => 
            chatbot.allowedAssessments.includes(id)
          );

          return {
            ...chatbot,
            allowedAssessments: allSelected
              ? chatbot.allowedAssessments.filter(id => !assessmentsToToggle.includes(id))
              : Array.from(new Set([...chatbot.allowedAssessments, ...assessmentsToToggle]))
          };
        }
        return chatbot;
      })
    );
  };

  const toggleSection = (chatbotId: string, section: 'wellness' | 'learn') => {
    setExpandedSections(prev => ({
      ...prev,
      [chatbotId]: {
        wellness: prev[chatbotId]?.wellness || false,
        learn: prev[chatbotId]?.learn || false,
        [section]: !(prev[chatbotId]?.[section] || false)
      }
    }));
  };

  const savePermissions = async () => {
    setIsSaving(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setErrorMessage('No authentication token found');
        return;
      }

      const response = await fetch(API_CONFIG.ENDPOINTS.DATA_PERMISSIONS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({ permissions })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccessMessage('Global data permissions saved successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setErrorMessage(data.message || 'Failed to save permissions. Please try again.');
      }
    } catch (error) {

      setErrorMessage('An error occurred while saving permissions.');
    } finally {
      setIsSaving(false);
    }
  };

  const resetPermissions = async () => {
    // Ask for confirmation before resetting
    const confirmed = window.confirm(
      'Are you sure you want to reset all permissions to defaults? This will:\n' +
      '- Delete all existing permission data\n' +
      '- Set up the correct 12 coaches (LIFE PSENsOR, Physical Fitness Coach, Wellness Coach, Spiritual Coach, Nutritional Coach, Emotio-Psycho-Rational Coach, Legendarial Coach, Religio-Traditio-Cultural Coach, Socio-Rational Coach, Financial Coach, Environmental Coach, Occupational Coach)\n' +
      '- Clear any old separate "Fitness Coach" and "Dietitian" entries\n\n' +
      'This action cannot be undone.'
    );
    
    if (!confirmed) {
      return;
    }

    setIsSaving(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setErrorMessage('No authentication token found');
        return;
      }

      const response = await fetch(`${API_CONFIG.ENDPOINTS.DATA_PERMISSIONS}/reset`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data && data.data.permissions) {
          setPermissions(data.data.permissions);
          setSuccessMessage('Permissions reset to defaults successfully! Old data cleared.');
          setTimeout(() => setSuccessMessage(''), 3000);
          
          // Force a page refresh to ensure clean state
          setTimeout(() => {
            window.location.reload();
          }, 2000);
        } else {
          setErrorMessage(data.message || 'Failed to reset permissions');
        }
      } else {
        const errorData = await response.json();
        setErrorMessage(errorData.message || 'Failed to reset permissions');
      }
    } catch (error) {

      setErrorMessage('Network error while resetting permissions');
    } finally {
      setIsSaving(false);
    }
  };

  const loadRecommendedPermissions = () => {
    const recommended: ChatbotPermission[] = [
      {
        chatbotId: 'life-psensor',
        chatbotName: 'LIFE PSENsOR',
        icon: '🤖',
        allowedAssessments: [
          'physical-health', 'mental-health', 'emotional-wellbeing', 'blood-test',
          'spiritual', 'nutritional', 'emotio-psycho-rational',
          'legendarial', 'financial', 'religio-traditio-cultural', 'environmental',
          'socio-relational', 'occupational'
        ]
      },
      {
        chatbotId: 'fitness-diet',
        chatbotName: 'Physical Fitness Coach',
        icon: '💪',
        allowedAssessments: ['physical-health', 'blood-test', 'nutritional', 'financial', 'environmental', 'occupational']
      },
      {
        chatbotId: 'wellness',
        chatbotName: 'Wellness Coach',
        icon: '🧘',
        allowedAssessments: [
          'mental-health', 'emotional-wellbeing', 'spiritual',
          'emotio-psycho-rational', 'legendarial', 'financial', 'religio-traditio-cultural',
          'environmental', 'socio-relational', 'occupational'
        ]
      },
      {
        chatbotId: 'spiritual',
        chatbotName: 'Spiritual Coach',
        icon: '🕊️',
        allowedAssessments: ['spiritual']
      },
      {
        chatbotId: 'nutritional',
        chatbotName: 'Nutritional Coach',
        icon: '🥗',
        allowedAssessments: ['nutritional']
      },
      {
        chatbotId: 'emotio_psycho_rational',
        chatbotName: 'Emotio-Psycho-Rational Coach',
        icon: '🧩',
        allowedAssessments: ['emotio-psycho-rational']
      },
      {
        chatbotId: 'legendarial',
        chatbotName: 'Legendarial Coach',
        icon: '🛏️',
        allowedAssessments: ['legendarial']
      },
      {
        chatbotId: 'religio_traditio_cultural',
        chatbotName: 'Religio-Traditio-Cultural Coach',
        icon: '🕯️',
        allowedAssessments: ['religio-traditio-cultural']
      },
      {
        chatbotId: 'socio_relational',
        chatbotName: 'Socio-Relational Coach',
        icon: '🗣️',
        allowedAssessments: ['socio-relational']
      },
      {
        chatbotId: 'financial',
        chatbotName: 'Financial Coach',
        icon: '💰',
        allowedAssessments: ['financial']
      },
      {
        chatbotId: 'environmental',
        chatbotName: 'Environmental Coach',
        icon: '🌍',
        allowedAssessments: ['environmental']
      },
      {
        chatbotId: 'occupational',
        chatbotName: 'Occupational Coach',
        icon: '💼',
        allowedAssessments: ['occupational']
      }
    ];
    setPermissions(recommended);
  };

  if (isLoading) {
    return (
      <div className="data-permissions-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading permissions...</p>
        </div>
      </div>
    );
  }

  const healthAssessments = assessmentTypes.filter(a => a.category === 'wellness');
  const learnAssessments = assessmentTypes.filter(a => a.category === 'learn');

  return (
    <div className="data-permissions-page">
      <div className="permissions-header">
        <div className="header-content">
          <h1>Data Permissions Control</h1>
          <p className="subtitle">Configure which assessment data each chatbot can access</p>
        </div>
        <div className="header-actions">
          <button className="btn-secondary" onClick={loadRecommendedPermissions}>
            Load Recommended
          </button>
          <button className="btn-warning" onClick={resetPermissions} disabled={isSaving}>
            {isSaving ? 'Resetting...' : 'Reset to Defaults'}
          </button>
          <button className="btn-primary" onClick={savePermissions} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save Permissions'}
          </button>
        </div>
      </div>

      {successMessage && (
        <div className="alert alert-success">
          {successMessage}
        </div>
      )}

      {errorMessage && (
        <div className="alert alert-error">
          {errorMessage}
        </div>
      )}

      <div className="permissions-grid">
        {permissions.map(chatbot => (
          <div key={chatbot.chatbotId} className="chatbot-card">
            <div className="chatbot-header">
              <div className="chatbot-info">
                <span className="chatbot-icon">
                  {chatbot.avatarUrl ? (
                    <img src={chatbot.avatarUrl} alt={chatbot.chatbotName} className="chatbot-icon-img" />
                  ) : (
                    chatbot.icon
                  )}
                </span>
                <h2>{chatbot.chatbotName}</h2>
              </div>
              <div className="permission-count">
                {chatbot.allowedAssessments.length} / {assessmentTypes.length} assessments
              </div>
            </div>

            <div className="bulk-actions">
              <button 
                className="bulk-btn"
                onClick={() => toggleAllForChatbot(chatbot.chatbotId, 'wellness')}
              >
                Toggle Wellness
              </button>
              <button 
                className="bulk-btn"
                onClick={() => toggleAllForChatbot(chatbot.chatbotId, 'learn')}
              >
                Toggle Learn
              </button>
              <button 
                className="bulk-btn"
                onClick={() => toggleAllForChatbot(chatbot.chatbotId, 'all')}
              >
                Toggle All
              </button>
            </div>

            {/* Wellness Assessments Section */}
            <div className="assessment-category">
              <div className="category-header" onClick={() => toggleSection(chatbot.chatbotId, 'wellness')}>
                <h3 className="category-title">Wellness Tab Assessments</h3>
                <span className="dropdown-arrow">
                  {(expandedSections[chatbot.chatbotId]?.wellness || false) ? <span className="material-symbols-outlined">expand_less</span> : <span className="material-symbols-outlined">expand_more</span>}
                </span>
              </div>
              <div className={`assessment-list ${(expandedSections[chatbot.chatbotId]?.wellness || false) ? 'expanded' : 'collapsed'}`}>
                {healthAssessments.map(assessment => {
                  const isAllowed = chatbot.allowedAssessments.includes(assessment.id);
                  return (
                    <div 
                      key={assessment.id} 
                      className={`assessment-item ${isAllowed ? 'allowed' : 'denied'}`}
                    >
                      <div className="checkbox-wrapper">
                        <input 
                          type="checkbox" 
                          checked={isAllowed} 
                          onChange={() => toggleAssessment(chatbot.chatbotId, assessment.id)}
                          id={`${chatbot.chatbotId}-${assessment.id}`} 
                        />
                        <label htmlFor={`${chatbot.chatbotId}-${assessment.id}`}>
                          <span className="assessment-name">{assessment.name}</span>
                          <span className="assessment-description">{assessment.description}</span>
                        </label>
                      </div>
                      <div className={`status-badge ${isAllowed ? 'status-allowed' : 'status-denied'}`}>
                        {isAllowed ? <span className="material-symbols-outlined">check</span> : <span className="material-symbols-outlined">close</span>} {isAllowed ? 'Allowed' : 'Denied'}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Learn Assessments Section */}
            <div className="assessment-category">
              <div className="category-header" onClick={() => toggleSection(chatbot.chatbotId, 'learn')}>
                <h3 className="category-title">Learn Tab Assessments</h3>
                <span className="dropdown-arrow">
                  {(expandedSections[chatbot.chatbotId]?.learn || false) ? <span className="material-symbols-outlined">expand_less</span> : <span className="material-symbols-outlined">expand_more</span>}
                </span>
              </div>
              <div className={`assessment-list ${(expandedSections[chatbot.chatbotId]?.learn || false) ? 'expanded' : 'collapsed'}`}>
                {learnAssessments.map(assessment => {
                  const isAllowed = chatbot.allowedAssessments.includes(assessment.id);
                  return (
                    <div 
                      key={assessment.id} 
                      className={`assessment-item ${isAllowed ? 'allowed' : 'denied'}`}
                    >
                      <div className="checkbox-wrapper">
                        <input 
                          type="checkbox" 
                          checked={isAllowed} 
                          onChange={() => toggleAssessment(chatbot.chatbotId, assessment.id)}
                          id={`${chatbot.chatbotId}-${assessment.id}`} 
                        />
                        <label htmlFor={`${chatbot.chatbotId}-${assessment.id}`}>
                          <span className="assessment-name">{assessment.name}</span>
                          <span className="assessment-description">{assessment.description}</span>
                        </label>
                      </div>
                      <div className={`status-badge ${isAllowed ? 'status-allowed' : 'status-denied'}`}>
                        {isAllowed ? <span className="material-symbols-outlined">check</span> : <span className="material-symbols-outlined">close</span>} {isAllowed ? 'Allowed' : 'Denied'}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="info-section">
        <h3><span className="material-symbols-outlined">info</span> How it works</h3>
        <ul>
          <li><strong>LIFE PSENsOR (Main AI):</strong> Comprehensive wellness coordinator - has access to all assessments for holistic guidance</li>
          <li><strong>Physical Fitness Coach:</strong> Physical wellness and nutrition focused - needs physical wellness, blood test, and nutritional data</li>
          <li><strong>Wellness Coach:</strong> Mental wellness focused - needs mental wellness and emotional wellbeing assessments</li>
          <li><strong>Spiritual Coach:</strong> Spiritual wellbeing focused - needs spiritual assessment data for guidance on meaning and purpose</li>
          <li><strong>Nutritional Coach:</strong> Diet quality and nutrition focused - needs nutritional assessment data for personalized meal planning</li>
          <li><strong>Emotio-Psycho-Rational Coach:</strong> Emotional and psychological wellbeing focused - needs DASS-21, PHQ-9, GAD-7 assessments for emotional pattern analysis</li>
          <li><strong>Legendarial Coach:</strong> Heritage, identity, and cultural narrative focused - needs legendarial assessment data for legacy and life purpose guidance</li>
          <li><strong>Religio-Traditio-Cultural Coach:</strong> Religious, traditional, and cultural practices focused - needs religio-traditio-cultural assessment data for understanding religious and cultural wellbeing</li>
          <li><strong>Socio-Relational Coach:</strong> Relationships, social dynamics, and relational decision-making focused - needs socio-relational assessment data for understanding social patterns and decision-making</li>
          <li><strong>Financial Coach:</strong> Financial wellness and money management focused - needs financial assessment data for budgeting and financial planning guidance</li>
          <li><strong>Environmental Coach:</strong> Environmental quality of life focused - needs environmental assessment data for living conditions and sustainability guidance</li>
          <li><strong>Occupational Coach:</strong> Work-life balance and career satisfaction focused - needs occupational assessment data for career development and work wellbeing guidance</li>
        </ul>
        <p className="note">💡 <strong>Tip:</strong> Limiting data access reduces token usage and costs while improving chatbot specialization.</p>
      </div>
    </div>
  );
};

export default DataPermissionsPage;



